import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import {
  Mail, Loader2, CheckCircle2, AlertTriangle, RefreshCw, Trash2,
  Plug, Inbox as InboxIcon, KeyRound, Shield, Plus, X,
  ChevronDown, ChevronUp, Pencil, Eye, History, FileText, Filter,
} from "lucide-react";
import { toast } from "sonner";
import AppPasswordTutorial from "../components/AppPasswordTutorial";

const EMPTY = {
  host: "imap.ionos.es",
  port: 993,
  use_ssl: true,
  username: "",
  password: "",
  folder: "INBOX",
  processed_folder: "HOSYS-Processat",
  keyword: "factura",
  enabled: true,
  max_per_run: 25,
  allowed_senders: [],
  label: "",
  only_unseen: true,
  lookback_days: 30,
};

export default function Mailbox() {
  const { lang } = useI18n();
  const tr = (ca, es) => (lang === "es" ? es : ca);
  const [boxes, setBoxes] = useState([]);
  const [featureEnabled, setFeatureEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [history, setHistory] = useState([]);
  const [imports, setImports] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showImports, setShowImports] = useState(true);
  const [batchOcrRunning, setBatchOcrRunning] = useState(false);

  // Pending OCR count: any imported invoice where supplier_name is empty.
  const pendingOCR = imports.filter((i) => !i.supplier_name).length;

  const runBatchOCR = async () => {
    if (batchOcrRunning) return;
    if (!window.confirm(tr(
      `Vols executar OCR sobre ${pendingOCR} factures pendents? És GRATUÏT (Tesseract local) i triga uns segons per factura.`,
      `¿Ejecutar OCR sobre ${pendingOCR} facturas pendientes? Es GRATIS (Tesseract local) y tarda unos segundos por factura.`
    ))) return;
    setBatchOcrRunning(true);
    try {
      // engine=free → Tesseract local, $0 cost. The user can still hit the
      // per-invoice "Re-escanejar" button for higher quality via cloud OCR.
      const r = await api.post("/received-invoices/process-pending?engine=free&limit=100");
      const d = r.data;
      toast.success(tr(
        `OCR completat (gratuït): ${d.ok} factures · ${d.failed} errors`,
        `OCR completado (gratis): ${d.ok} facturas · ${d.failed} errores`
      ));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBatchOcrRunning(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/mailbox/list");
      setBoxes(r.data.mailboxes || []);
      setFeatureEnabled(r.data.feature_enabled !== false);
      // Aggregate history + imports across all mailboxes.
      const [h, im] = await Promise.all([
        api.get("/mailbox/history?limit=20").catch(() => ({ data: { runs: [] } })),
        api.get("/mailbox/imports?limit=30").catch(() => ({ data: { imports: [] } })),
      ]);
      setHistory(h.data.runs || []);
      setImports(im.data.imports || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const syncOne = async (mb) => {
    setSyncingId(mb.id);
    try {
      const r = await api.post(`/mailbox/${mb.id}/sync-now`);
      if (r.data.ok === false) {
        toast.error(r.data.error || "Error");
      } else {
        const n = r.data.imported || 0;
        const s = r.data.skipped || 0;
        toast.success(tr(
          `${n} importada${n === 1 ? "" : "es"} · ${s} duplicada${s === 1 ? "" : "es"}`,
          `${n} importada${n === 1 ? "" : "s"} · ${s} duplicada${s === 1 ? "" : "s"}`
        ));
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSyncingId(null);
    }
  };

  const deleteOne = async (mb) => {
    if (!window.confirm(tr(
      `Vols eliminar la bústia "${mb.label}"? No es perden les factures importades.`,
      `¿Eliminar el buzón "${mb.label}"? No se pierden las facturas importadas.`
    ))) return;
    try {
      await api.delete(`/mailbox/${mb.id}`);
      toast.success(tr("Eliminada", "Eliminada"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  if (loading) {
    return (
      <div className="card-surface p-12 text-center text-slate-400">
        <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
        {tr("Carregant…", "Cargando…")}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mailbox-page">
      {/* Hero */}
      <div className="card-surface p-5 sm:p-6 bg-gradient-to-br from-blue-500/10 via-transparent to-emerald-500/10">
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
            <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-blue-300" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight">
              {tr("Bústies automàtiques", "Buzones automáticos")}
            </h1>
            <p className="text-xs sm:text-sm text-slate-400 mt-1 leading-relaxed max-w-2xl">
              {tr(
                "Connecta els teus correus i les factures que rebis amb la paraula \"factura\" al subject o cos s'incorporaran soles. Reviso cada 60 min.",
                "Conecta tus correos y las facturas que recibas con la palabra \"factura\" en el asunto o cuerpo se incorporarán solas. Reviso cada 60 min."
              )}
            </p>
          </div>
        </div>
        <Button
          onClick={() => setEditing({ ...EMPTY })}
          disabled={!featureEnabled}
          data-testid="mailbox-new-btn"
          className="w-full sm:w-auto mt-4 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400"
        >
          <Plus size={14} className="mr-2" />
          {tr("Afegir bústia", "Añadir buzón")}
        </Button>
      </div>

      {!featureEnabled && (
        <div className="card-surface p-4 border-amber-500/40 bg-amber-500/5">
          <div className="flex items-start gap-3 text-sm text-amber-200">
            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
            <p>{tr(
              "El servidor no té MAILBOX_ENCRYPTION_KEY. Demana al teu administrador que l'activi.",
              "El servidor no tiene MAILBOX_ENCRYPTION_KEY. Pide a tu administrador que la active."
            )}</p>
          </div>
        </div>
      )}

      {/* Mailbox list */}
      {boxes.length === 0 ? (
        <div className="card-surface p-12 text-center" data-testid="mailbox-empty">
          <InboxIcon className="w-12 h-12 mx-auto mb-3 text-slate-500" />
          <p className="text-slate-300 font-medium">
            {tr("Cap bústia configurada encara", "Ningún buzón configurado todavía")}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {tr("Clica \"Afegir bústia\" per començar.", "Pulsa \"Añadir buzón\" para empezar.")}
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="mailbox-list">
          {boxes.map((mb) => (
            <MailboxCard
              key={mb.id}
              mb={mb}
              onEdit={() => setEditing(mb)}
              onDelete={() => deleteOne(mb)}
              onSync={() => syncOne(mb)}
              syncing={syncingId === mb.id}
              tr={tr}
              lang={lang}
            />
          ))}
        </div>
      )}

      {/* Imports — recently auto-pulled invoices */}
      {boxes.length > 0 && (
        <div className="card-surface overflow-hidden" data-testid="mailbox-imports">
          <button
            type="button"
            onClick={() => setShowImports(!showImports)}
            className="w-full px-4 sm:px-5 py-3 flex items-center justify-between bg-white/[0.02] hover:bg-white/[0.04]"
          >
            <h3 className="text-xs uppercase tracking-wider text-slate-300 font-semibold flex items-center gap-2">
              <FileText size={12} className="text-emerald-300" />
              {tr("Factures importades per bústia", "Facturas importadas por buzón")}
              <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-200 text-[10px] border border-emerald-500/30">
                {imports.length}
              </span>
              {pendingOCR > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-200 text-[10px] border border-amber-500/30 flex items-center gap-1">
                  <AlertTriangle size={9} /> {pendingOCR} {tr("pendents OCR", "pendientes OCR")}
                </span>
              )}
            </h3>
            {showImports ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
          </button>
          {showImports && pendingOCR > 0 && (
            <div className="px-4 sm:px-5 py-3 bg-amber-500/[0.04] border-t border-amber-500/20 flex items-center gap-3 flex-wrap">
              <div className="text-xs text-amber-200 flex-1 min-w-0">
                {tr(
                  `Hi ha ${pendingOCR} factures importades sense processar. Executa OCR per omplir proveïdor, NIF i imports.`,
                  `Hay ${pendingOCR} facturas importadas sin procesar. Ejecuta OCR para rellenar proveedor, NIF e importes.`
                )}
                <span className="block text-[10px] text-emerald-300 mt-1">
                  {tr("OCR gratuït amb Tesseract local · 0€ cost API",
                      "OCR gratis con Tesseract local · 0€ coste API")}
                </span>
              </div>
              <Button
                onClick={runBatchOCR}
                disabled={batchOcrRunning}
                data-testid="mailbox-batch-ocr-btn"
                className="bg-amber-600 hover:bg-amber-500 text-white"
                size="sm"
              >
                {batchOcrRunning ? <Loader2 size={13} className="mr-2 animate-spin" /> : <Eye size={13} className="mr-2" />}
                {tr(`Processar ${pendingOCR} pendents`, `Procesar ${pendingOCR} pendientes`)}
              </Button>
            </div>
          )}
          {showImports && (
            imports.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500 border-t border-white/5">
                {tr(
                  "Cap factura encara. Quan el sistema en trobi alguna l'afegirà aquí.",
                  "Ninguna factura todavía. Cuando el sistema encuentre alguna la añadirá aquí."
                )}
              </div>
            ) : (
              <div className="overflow-x-auto border-t border-white/5">
                <table className="w-full text-xs">
                  <thead className="bg-white/[0.02]">
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                      <th className="text-left p-3">{tr("Quan", "Cuando")}</th>
                      <th className="text-left p-3">{tr("Proveïdor", "Proveedor")}</th>
                      <th className="text-left p-3 hidden sm:table-cell">{tr("Núm.", "Núm.")}</th>
                      <th className="text-right p-3">{tr("Total", "Total")}</th>
                      <th className="text-center p-3 hidden md:table-cell">{tr("Estat", "Estado")}</th>
                      <th className="text-left p-3 hidden lg:table-cell">{tr("De", "De")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {imports.map((inv) => (
                      <tr
                        key={inv.id}
                        className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer"
                        onClick={() => window.location.href = `/app/received-invoices?open=${inv.id}`}
                        data-testid={`mailbox-import-${inv.id}`}
                      >
                        <td className="p-3 text-slate-400 whitespace-nowrap">
                          {inv.created_at ? new Date(inv.created_at).toLocaleDateString(lang === "es" ? "es-ES" : "ca-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                        </td>
                        <td className="p-3 font-medium text-slate-200">
                          {inv.supplier_name || <span className="text-amber-300 text-[10px] italic">{tr("Pendent OCR", "Pendiente OCR")}</span>}
                          <div className="text-[10px] text-slate-500 sm:hidden truncate">
                            {inv.from || inv.file_name}
                          </div>
                        </td>
                        <td className="p-3 text-slate-400 font-mono hidden sm:table-cell">{inv.document_number || "—"}</td>
                        <td className="p-3 text-right font-mono text-slate-200">
                          {inv.total ? `${Number(inv.total).toFixed(2)} €` : "—"}
                        </td>
                        <td className="p-3 text-center hidden md:table-cell">
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] border ${
                            inv.status === "approved" ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/30" :
                            inv.status === "pending" ? "bg-amber-500/15 text-amber-200 border-amber-500/30" :
                            "bg-slate-500/15 text-slate-300 border-slate-500/30"
                          }`}>
                            {inv.status}
                          </span>
                        </td>
                        <td className="p-3 text-slate-500 font-mono text-[10px] truncate max-w-[200px] hidden lg:table-cell">
                          {inv.from}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      )}

      {/* Aggregated history of scans */}
      {boxes.length > 0 && history.length > 0 && (
        <div className="card-surface overflow-hidden" data-testid="mailbox-aggregate-history">
          <button
            type="button"
            onClick={() => setShowHistory(!showHistory)}
            className="w-full px-4 sm:px-5 py-3 flex items-center justify-between bg-white/[0.02] hover:bg-white/[0.04]"
          >
            <h3 className="text-xs uppercase tracking-wider text-slate-300 font-semibold flex items-center gap-2">
              <History size={12} className="text-blue-300" />
              {tr("Historial d'escaneigs", "Historial de escaneos")}
              <span className="px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-200 text-[10px] border border-blue-500/30">
                {history.length}
              </span>
            </h3>
            {showHistory ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
          </button>
          {showHistory && (
            <div className="overflow-x-auto border-t border-white/5">
              <table className="w-full text-xs">
                <thead className="bg-white/[0.02]">
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="text-left p-3">{tr("Quan", "Cuando")}</th>
                    <th className="text-left p-3 hidden sm:table-cell">{tr("Bústia", "Buzón")}</th>
                    <th className="text-center p-3">{tr("Revisats", "Revisados")}</th>
                    <th className="text-center p-3">{tr("Importats", "Importados")}</th>
                    <th className="text-center p-3 hidden sm:table-cell">{tr("Duplicats", "Duplicados")}</th>
                    <th className="text-center p-3 hidden md:table-cell">{tr("Errors", "Errores")}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((r, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="p-3 text-slate-400 whitespace-nowrap">
                        {new Date(r.at).toLocaleString(lang === "es" ? "es-ES" : "ca-ES")}
                      </td>
                      <td className="p-3 text-slate-300 font-mono hidden sm:table-cell">{r.username || "—"}</td>
                      <td className="p-3 text-center text-slate-400">{r.checked}</td>
                      <td className="p-3 text-center text-emerald-300 font-mono font-semibold">{r.imported}</td>
                      <td className="p-3 text-center text-slate-400 font-mono hidden sm:table-cell">{r.skipped}</td>
                      <td className="p-3 text-center text-rose-300 font-mono hidden md:table-cell">{r.errors || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Editor modal */}
      {editing && (
        <MailboxEditor
          initial={editing}
          isNew={!editing.id}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
          tr={tr}
          lang={lang}
        />
      )}
    </div>
  );
}

/* ---------- Card per single mailbox ---------- */
function MailboxCard({ mb, onEdit, onDelete, onSync, syncing, tr, lang }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="card-surface overflow-hidden"
      data-testid={`mailbox-card-${mb.id}`}
    >
      <div className="p-4 sm:p-5 flex flex-wrap items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
          ${mb.enabled ? "bg-emerald-500/15 border border-emerald-500/30" : "bg-slate-500/15 border border-slate-500/30"}`}>
          <Mail className={`w-5 h-5 ${mb.enabled ? "text-emerald-300" : "text-slate-400"}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-base text-white truncate">{mb.label}</h3>
            {!mb.enabled && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-500/15 text-slate-300 border border-slate-500/30">
                {tr("Pausada", "Pausado")}
              </span>
            )}
            {mb.allowed_senders.length > 0 && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-200 border border-emerald-500/30 flex items-center gap-1">
                <Shield size={9} /> {mb.allowed_senders.length}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-0.5 font-mono truncate">{mb.username}</p>
          <p className="text-[10px] text-slate-500 mt-0.5">
            {mb.host}:{mb.port} · {tr("paraula clau", "palabra clave")}: <span className="font-mono text-slate-300">{mb.keyword}</span>
          </p>
        </div>
        <div className="flex gap-1.5 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={onSync}
            disabled={syncing || !mb.enabled}
            data-testid={`mailbox-sync-${mb.id}`}
            className="h-9 border-emerald-500/30 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/20"
          >
            {syncing ? <Loader2 size={13} className="animate-spin sm:mr-2" /> : <RefreshCw size={13} className="sm:mr-2" />}
            <span className="hidden sm:inline">{tr("Sincronitzar", "Sincronizar")}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
            data-testid={`mailbox-edit-${mb.id}`}
            className="h-9 border-white/10 hover:bg-white/5"
          >
            <Pencil size={13} className="sm:mr-2" />
            <span className="hidden sm:inline">{tr("Editar", "Editar")}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onDelete}
            data-testid={`mailbox-delete-${mb.id}`}
            className="h-9 border-rose-500/30 bg-rose-500/5 text-rose-200 hover:bg-rose-500/20"
          >
            <Trash2 size={13} />
          </Button>
        </div>
      </div>
      {/* Collapsible details */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={`mailbox-toggle-${mb.id}`}
        className="w-full px-4 sm:px-5 py-2.5 text-xs uppercase tracking-wider text-slate-400 hover:text-white border-t border-white/5 flex items-center justify-between bg-white/[0.02]"
      >
        <span className="flex items-center gap-2">
          {mb.last_run_at ? (
            mb.last_run_status === "ok"
              ? <CheckCircle2 size={12} className="text-emerald-400" />
              : <AlertTriangle size={12} className="text-rose-400" />
          ) : null}
          {mb.last_run_at
            ? `${tr("Últim escaneig", "Último escaneo")}: ${new Date(mb.last_run_at).toLocaleString(lang === "es" ? "es-ES" : "ca-ES")}`
            : tr("Sense escanejos encara", "Sin escaneos todavía")}
        </span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="p-4 sm:p-5 border-t border-white/5 bg-white/[0.01] text-xs space-y-2">
          {mb.last_run_summary && (
            <div className="flex flex-wrap gap-2">
              <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-200 border border-emerald-500/30">
                {mb.last_run_summary.imported || 0} {tr("importades", "importadas")}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-slate-500/15 text-slate-300 border border-slate-500/30">
                {mb.last_run_summary.skipped || 0} {tr("duplicades", "duplicadas")}
              </span>
              {(mb.last_run_summary.errors || 0) > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-200 border border-rose-500/30">
                  {mb.last_run_summary.errors} {tr("errors", "errores")}
                </span>
              )}
            </div>
          )}
          {mb.last_run_error && (
            <div className="text-rose-300 font-mono text-[11px] bg-rose-500/5 border border-rose-500/20 rounded p-2">
              {mb.last_run_error}
            </div>
          )}
          {mb.allowed_senders.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                {tr("Remitents permesos", "Remitentes permitidos")}
              </p>
              <div className="flex flex-wrap gap-1">
                {mb.allowed_senders.map((s, i) => (
                  <span key={i} className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-200">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
          <p className="text-[10px] text-slate-500">
            {tr("Carpeta destí processats", "Carpeta destino procesados")}: <span className="font-mono text-slate-300">{mb.processed_folder}</span>
          </p>
        </div>
      )}
    </div>
  );
}

/* ---------- Modal editor (create + update) ---------- */
function MailboxEditor({ initial, isNew, onClose, onSaved, tr, lang }) {
  const [form, setForm] = useState({ ...EMPTY, ...initial, password: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [draft, setDraft] = useState("");
  const [showAppPwdTutorial, setShowAppPwdTutorial] = useState(false);
  const [appPwdProvider, setAppPwdProvider] = useState("gmail");

  const preview = async () => {
    if (previewing || isNew) {
      if (isNew) toast.message(tr("Desa-la primer per fer vista prèvia",
                                   "Guárdalo primero para hacer vista previa"));
      return;
    }
    setPreviewing(true);
    setPreviewResult(null);
    try {
      const r = await api.post(`/mailbox/${initial.id}/preview`);
      setPreviewResult(r.data);
      if (r.data.ok === false) {
        toast.error(r.data.error || "Error");
      } else {
        const m = r.data.matched || 0;
        toast.success(tr(
          `${r.data.checked} correus revisats · ${m} amb la paraula clau`,
          `${r.data.checked} correos revisados · ${m} con la palabra clave`
        ));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setPreviewing(false);
    }
  };

  const addSender = () => {
    const v = draft.trim().toLowerCase();
    if (!v) return;
    if (form.allowed_senders.includes(v)) {
      toast.message(tr("Ja existeix", "Ya existe"));
      setDraft("");
      return;
    }
    setForm((f) => ({ ...f, allowed_senders: [...f.allowed_senders, v] }));
    setDraft("");
  };
  const removeSender = (i) =>
    setForm((f) => ({ ...f, allowed_senders: f.allowed_senders.filter((_, j) => j !== i) }));

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const draftVal = draft.trim().toLowerCase();
      let payload = { ...form };
      if (draftVal && !payload.allowed_senders.includes(draftVal)) {
        payload.allowed_senders = [...payload.allowed_senders, draftVal];
      }
      if (!payload.password) delete payload.password;
      if (!payload.label) payload.label = payload.username;
      if (isNew) {
        await api.post("/mailbox/create", payload);
      } else {
        await api.put(`/mailbox/${initial.id}`, payload);
      }
      toast.success(tr("Bústia desada", "Buzón guardado"));
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const testConn = async () => {
    if (testing) return;
    setTesting(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      const r = await api.post("/mailbox/test", payload);
      toast.success(tr(
        `Connexió OK — ${r.data.unread} sense llegir a ${r.data.folder}`,
        `Conexión OK — ${r.data.unread} sin leer en ${r.data.folder}`
      ));
    } catch (e) {
      const detail = e?.response?.data?.detail || "Error";
      toast.error(detail);
      // Auto-open the App-Password tutorial when the hint we got back from
      // the server points at Gmail/Outlook authentication — saves the user
      // from hunting down the right Google panel.
      const lower = String(detail).toLowerCase();
      const username = (form.username || "").toLowerCase();
      const looksGmail = username.endsWith("@gmail.com")
        || username.endsWith("@googlemail.com")
        || lower.includes("gmail");
      const looksOutlook = username.endsWith("@outlook.com")
        || username.endsWith("@hotmail.com")
        || username.endsWith("@live.com");
      const isAuthError = lower.includes("contrasenya d'aplicació")
        || lower.includes("contraseña de aplicación")
        || lower.includes("app password")
        || lower.includes("authenticationfailed")
        || lower.includes("application-specific");
      if (isAuthError && (looksGmail || looksOutlook)) {
        setAppPwdProvider(looksOutlook ? "outlook" : "gmail");
        // Delay so the toast renders first then the dialog steals focus
        setTimeout(() => setShowAppPwdTutorial(true), 600);
      }
    } finally {
      setTesting(false);
    }
  };

  return (
    <>
    <AppPasswordTutorial
      open={showAppPwdTutorial}
      onClose={() => setShowAppPwdTutorial(false)}
      provider={appPwdProvider}
    />
    <div
      className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-150"
      onClick={(saving || testing) ? undefined : onClose}
      data-testid="mailbox-editor-overlay"
    >
      <div
        className="w-full sm:max-w-2xl bg-[#0B1220] sm:border border-white/10 sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh] sm:max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
        data-testid="mailbox-editor"
      >
        <div className="p-4 sm:p-5 border-b border-white/10 flex items-center gap-3 flex-shrink-0">
          <Mail size={18} className="text-blue-300" />
          <h2 className="font-display text-lg font-semibold flex-1">
            {isNew ? tr("Afegir bústia", "Añadir buzón") : tr("Editar bústia", "Editar buzón")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="mailbox-editor-close"
            className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          {/* Label */}
          <Field label={tr("Nom (per identificar-la)", "Nombre (para identificarla)")}>
            <Input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder={tr("Ex: Gerencia IONOS", "Ej: Gerencia IONOS")}
              data-testid="mb-label"
              className="bg-[#05070D] border-white/10 h-11"
            />
          </Field>

          {/* Server */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={tr("Servidor", "Servidor")} required>
              <Input value={form.host}
                onChange={(e) => setForm({ ...form, host: e.target.value })}
                data-testid="mb-host" className="bg-[#05070D] border-white/10 h-11" />
            </Field>
            <Field label={tr("Port", "Puerto")} required>
              <Input type="number" value={form.port}
                onChange={(e) => setForm({ ...form, port: Number(e.target.value) || 993 })}
                data-testid="mb-port" className="bg-[#05070D] border-white/10 h-11" />
            </Field>
            <Field label={tr("Adreça d'email", "Dirección de email")} required>
              <Input type="email" value={form.username}
                onChange={(e) => {
                  const v = e.target.value;
                  // Auto-detect well-known providers and fill IMAP host + port
                  // when those fields are still at their defaults — saves the
                  // user a manual lookup and prevents the SMTP:465 mistake.
                  const lower = v.toLowerCase();
                  const next = { ...form, username: v };
                  const presets = {
                    "@gmail.com":      { host: "imap.gmail.com",    port: 993 },
                    "@googlemail.com": { host: "imap.gmail.com",    port: 993 },
                    "@outlook.com":    { host: "imap-mail.outlook.com", port: 993 },
                    "@hotmail.com":    { host: "imap-mail.outlook.com", port: 993 },
                    "@live.com":       { host: "imap-mail.outlook.com", port: 993 },
                    "@yahoo.com":      { host: "imap.mail.yahoo.com", port: 993 },
                    "@yahoo.es":       { host: "imap.mail.yahoo.com", port: 993 },
                    "@ionos.es":       { host: "imap.ionos.es",      port: 993 },
                    "@ionos.com":      { host: "imap.ionos.com",     port: 993 },
                    "@icloud.com":     { host: "imap.mail.me.com",   port: 993 },
                  };
                  for (const [suffix, cfg] of Object.entries(presets)) {
                    if (lower.endsWith(suffix)) {
                      // Only overwrite when host hasn't been customised
                      // (i.e. still equals one of our defaults or empty).
                      if (!form.host || form.host === "imap.ionos.es"
                          || form.host === cfg.host) {
                        next.host = cfg.host;
                        next.port = cfg.port;
                        next.use_ssl = true;
                      }
                      break;
                    }
                  }
                  setForm(next);
                }}
                placeholder="factures@hosys.es"
                data-testid="mb-username" className="bg-[#05070D] border-white/10 h-11" />
            </Field>
            <Field label={tr("Contrasenya", "Contraseña") + (isNew ? "" : tr(" (buit = mantenir)", " (vacío = mantener)"))}
              required={isNew}>
              <Input type="password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={isNew ? "" : "••••••••"}
                data-testid="mb-password" className="bg-[#05070D] border-white/10 h-11" />
            </Field>
          </div>

          {/* Provider-specific App Password warning */}
          {form.username && (form.username.toLowerCase().endsWith("@gmail.com")
                              || form.username.toLowerCase().endsWith("@googlemail.com")) && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-3 text-xs text-amber-100"
                 data-testid="gmail-app-password-warning">
              <div className="flex items-center justify-between gap-2 mb-1">
                <strong>{tr("Gmail necessita contrasenya d'aplicació", "Gmail necesita contraseña de aplicación")}</strong>
                <button type="button"
                        onClick={() => { setAppPwdProvider("gmail"); setShowAppPwdTutorial(true); }}
                        className="text-amber-200 underline hover:text-amber-100 text-[11px] whitespace-nowrap"
                        data-testid="open-apppwd-tutorial-gmail">
                  {tr("Necessites ajuda?", "¿Necesitas ayuda?")}
                </button>
              </div>
              <div className="text-amber-200/80">
                {tr(
                  "Des de 2022 Gmail ja no accepta la contrasenya normal del compte. Cal:",
                  "Desde 2022 Gmail ya no acepta la contraseña normal de la cuenta. Necesitas:"
                )}
              </div>
              <ol className="list-decimal ml-5 mt-1 space-y-0.5 text-amber-200/80">
                <li>{tr("Activar la verificació en 2 passos", "Activar la verificación en 2 pasos")}</li>
                <li>
                  {tr("Anar a", "Ir a")}{" "}
                  <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer"
                     className="text-amber-200 underline hover:text-amber-100">
                    myaccount.google.com/apppasswords
                  </a>
                </li>
                <li>{tr("Generar una contrasenya per a 'Correu' i enganxar-la a dalt",
                       "Generar una contraseña para 'Correo' y pegarla arriba")}</li>
              </ol>
            </div>
          )}
          {form.username && (form.username.toLowerCase().endsWith("@outlook.com")
                              || form.username.toLowerCase().endsWith("@hotmail.com")) && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-3 text-xs text-amber-100"
                 data-testid="outlook-app-password-warning">
              <div className="flex items-center justify-between gap-2 mb-1">
                <strong>{tr("Outlook necessita contrasenya d'aplicació", "Outlook necesita contraseña de aplicación")}</strong>
                <button type="button"
                        onClick={() => { setAppPwdProvider("outlook"); setShowAppPwdTutorial(true); }}
                        className="text-amber-200 underline hover:text-amber-100 text-[11px] whitespace-nowrap"
                        data-testid="open-apppwd-tutorial-outlook">
                  {tr("Necessites ajuda?", "¿Necesitas ayuda?")}
                </button>
              </div>
              <div className="text-amber-200/80">
                {tr(
                  "Microsoft requereix una contrasenya d'aplicació per IMAP. Genera-la a ",
                  "Microsoft requiere una contraseña de aplicación para IMAP. Genérala en "
                )}
                <a href="https://account.live.com/proofs/AppPassword" target="_blank" rel="noreferrer"
                   className="text-amber-200 underline hover:text-amber-100">
                  account.live.com/proofs/AppPassword
                </a>
              </div>
            </div>
          )}

          <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
            <Switch checked={form.use_ssl}
              onCheckedChange={(v) => setForm({ ...form, use_ssl: v })}
              data-testid="mb-ssl" />
            {tr("Usar SSL/TLS (port 993)", "Usar SSL/TLS (puerto 993)")}
          </label>

          {/* Filter + folders */}
          <div className="pt-3 border-t border-white/5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label={tr("Paraula clau", "Palabra clave")}>
              <Input value={form.keyword}
                onChange={(e) => setForm({ ...form, keyword: e.target.value })}
                data-testid="mb-keyword" className="bg-[#05070D] border-white/10 h-11" />
            </Field>
            <Field label={tr("Carpeta entrada", "Carpeta entrada")}>
              <Input value={form.folder}
                onChange={(e) => setForm({ ...form, folder: e.target.value })}
                data-testid="mb-folder" className="bg-[#05070D] border-white/10 h-11" />
            </Field>
            <Field label={tr("Carpeta processats", "Carpeta procesados")}>
              <Input value={form.processed_folder}
                onChange={(e) => setForm({ ...form, processed_folder: e.target.value })}
                data-testid="mb-processed-folder" className="bg-[#05070D] border-white/10 h-11" />
            </Field>
          </div>

          {/* Scan strategy */}
          <div className="pt-3 border-t border-white/5">
            <div className="flex items-start gap-2 mb-2">
              <Filter size={14} className="text-blue-300 mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h3 className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                  {tr("Estratègia d'escaneig", "Estrategia de escaneo")}
                </h3>
                <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                  {tr(
                    "Si has obert algun correu al teu Gmail/Outlook, ja està marcat com a llegit i no l'agafarà si tens activat \"Només no llegits\". Desactiva-ho per escanejar TOTS els correus dels últims X dies.",
                    "Si has abierto algún correo en tu Gmail/Outlook, ya está marcado como leído y no lo cogerá si tienes activado \"Solo no leídos\". Desactívalo para escanear TODOS los correos de los últimos X días."
                  )}
                </p>
              </div>
            </div>
            <div className="space-y-3 mt-3">
              <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                <Switch checked={form.only_unseen}
                  onCheckedChange={(v) => setForm({ ...form, only_unseen: v })}
                  data-testid="mb-only-unseen" />
                {tr("Només correus no llegits (recomanat per defecte)",
                    "Solo correos no leídos (recomendado por defecto)")}
              </label>
              {!form.only_unseen && (
                <Field label={tr(`Buscar als últims ${form.lookback_days} dies`,
                                 `Buscar en los últimos ${form.lookback_days} días`)}>
                  <input
                    type="range"
                    min="1"
                    max="180"
                    value={form.lookback_days}
                    onChange={(e) => setForm({ ...form, lookback_days: Number(e.target.value) })}
                    data-testid="mb-lookback"
                    className="w-full accent-blue-500"
                  />
                </Field>
              )}
            </div>
          </div>

          {/* Whitelist */}
          <div className="pt-3 border-t border-white/5">
            <div className="flex items-start gap-2 mb-2">
              <Shield size={14} className="text-emerald-300 mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h3 className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                  {tr("Remitents permesos", "Remitentes permitidos")}
                </h3>
                <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                  {tr(
                    "Buida = accepta tots. Pots usar emails sencers (proveidor@acme.com) o dominis (acme.com).",
                    "Vacía = acepta todos. Puedes usar emails completos (proveedor@acme.com) o dominios (acme.com)."
                  )}
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-2">
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSender(); } }}
                placeholder={tr("proveidor@acme.com o acme.com",
                                "proveedor@acme.com o acme.com")}
                data-testid="mb-sender-input"
                className="bg-[#05070D] border-white/10 h-11 flex-1"
              />
              <Button
                type="button"
                onClick={addSender}
                disabled={!draft.trim()}
                data-testid="mb-sender-add-btn"
                className="bg-emerald-600 hover:bg-emerald-500 h-11"
              >
                <Plus size={14} className="sm:mr-2" />
                <span className="hidden sm:inline">{tr("Afegir", "Añadir")}</span>
              </Button>
            </div>
            {form.allowed_senders.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="mb-sender-chips">
                {form.allowed_senders.map((e, i) => (
                  <span
                    key={i}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-100 text-xs"
                  >
                    {e.includes("@") ? <Mail size={11} /> : <Shield size={11} />}
                    <span className="font-mono">{e}</span>
                    <button
                      type="button"
                      onClick={() => removeSender(i)}
                      aria-label="remove"
                      data-testid={`mb-sender-remove-${i}`}
                      className="hover:bg-emerald-500/30 rounded-full p-0.5 ml-0.5"
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Enabled */}
          <div className="pt-3 border-t border-white/5">
            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
              <Switch checked={form.enabled}
                onCheckedChange={(v) => setForm({ ...form, enabled: v })}
                data-testid="mb-enabled" />
              {tr("Bústia activa", "Buzón activo")}
            </label>
          </div>

          {/* Preview result */}
          {previewResult && previewResult.ok !== false && (
            <div className="pt-3 border-t border-white/5" data-testid="mb-preview-result">
              <h3 className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-2">
                <Eye size={12} /> {tr("Vista prèvia — què trobaria", "Vista previa — qué encontraría")}
              </h3>
              <div className="grid grid-cols-3 gap-2 text-center mb-3">
                <div className="card-surface p-2 bg-blue-500/5">
                  <div className="text-xs text-slate-400">{tr("Revisats", "Revisados")}</div>
                  <div className="font-mono text-lg text-blue-200">{previewResult.checked || 0}</div>
                </div>
                <div className="card-surface p-2 bg-emerald-500/5">
                  <div className="text-xs text-slate-400">{tr("Coincidents", "Coincidentes")}</div>
                  <div className="font-mono text-lg text-emerald-200">{previewResult.matched || 0}</div>
                </div>
                <div className="card-surface p-2 bg-amber-500/5">
                  <div className="text-xs text-slate-400">{tr("Amb adjunt", "Con adjunto")}</div>
                  <div className="font-mono text-lg text-amber-200">{previewResult.with_attachments || 0}</div>
                </div>
              </div>
              {previewResult.matched === 0 && previewResult.checked > 0 && (
                <div className="text-[11px] text-amber-200 bg-amber-500/5 border border-amber-500/30 rounded p-2 mb-2">
                  {tr(
                    `Cap dels ${previewResult.checked} correus té la paraula "${form.keyword}". Prova canviant la paraula clau, desactivant "Només no llegits" o ampliant la cerca.`,
                    `Ninguno de los ${previewResult.checked} correos tiene la palabra "${form.keyword}". Prueba cambiando la palabra clave, desactivando "Solo no leídos" o ampliando la búsqueda.`
                  )}
                </div>
              )}
              {previewResult.messages && previewResult.messages.length > 0 && (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {previewResult.messages.slice(0, 10).map((m, i) => (
                    <div key={i} className="text-[11px] p-2 rounded bg-white/[0.02] border border-white/5">
                      <div className="font-mono text-slate-300 truncate">{m.subject || "(sense assumpte)"}</div>
                      <div className="text-slate-500 truncate">{m.from}</div>
                      {m.attachment_names && m.attachment_names.length > 0 && (
                        <div className="text-emerald-300 mt-1 flex items-center gap-1">
                          <FileText size={10} /> {m.attachment_names.join(", ")}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="p-4 sm:p-5 border-t border-white/10 flex flex-col-reverse sm:flex-row gap-2 bg-[#08101D] flex-shrink-0">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving || testing}
            data-testid="mb-cancel-btn"
            className="border-white/10 h-11"
          >
            {tr("Cancel·lar", "Cancelar")}
          </Button>
          {!isNew && (
            <Button
              variant="outline"
              onClick={preview}
              disabled={previewing || saving}
              data-testid="mb-preview-btn"
              className="border-amber-500/30 bg-amber-500/10 text-amber-100 hover:bg-amber-500/20 h-11"
            >
              {previewing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Eye size={14} className="mr-2" />}
              {tr("Vista prèvia", "Vista previa")}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={testConn}
            disabled={testing || !form.username || (!form.password && isNew)}
            data-testid="mb-test-btn"
            className="border-blue-500/30 bg-blue-500/10 text-blue-100 hover:bg-blue-500/20 h-11"
          >
            {testing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Plug size={14} className="mr-2" />}
            {tr("Provar", "Probar")}
          </Button>
          <Button
            onClick={save}
            disabled={saving || !form.username}
            data-testid="mb-save-btn"
            className="flex-1 h-11 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400"
          >
            {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <KeyRound size={14} className="mr-2" />}
            {tr("Desar", "Guardar")}
          </Button>
        </div>
      </div>
    </div>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-slate-400 block mb-1.5">
        {label}
        {required && <span className="text-rose-400 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}
